/**
 * Vue2 Token管理：精准满足需求
 * 1. 单标签刷新 → 保留Token
 * 2. 多标签关单个 → 保留Token
 * 3. 浏览器关闭/仅一个标签关闭 → 删除Token
 * 4. 超时/主动登出 → 删除Token
 */
class TokenManager {
  constructor(options = {}) {
    this.config = {
      timeout: options.timeout || 300000, // 5分钟无操作超时
      tokenKey: options.tokenKey || "token",
      lastOperateKey: options.lastOperateKey || "vue2_last_operate",
      aliveTabsKey: options.aliveTabsKey || "vue2_alive_tab_ids", // 存活标签列表
      tabIdKey: "vue2_tab_id", // 当前标签ID（sessionStorage）
      cleanupDelay: 2000, // 关闭后延时清理阈值（2秒）
    };

    this.tabId = this.getTabId();
    this.idleTimer = null;
    this.isLoggedOut = false;
    this.cleanupTimer = null; // 延时清理定时器
    this.updateLastOperate = this.debounce(() => {
      localStorage.setItem(this.config.lastOperateKey, Date.now());
    }, 500);
  }

  init(Vue, router) {
    // 1. 启动核心校验：解决浏览器关闭后重启不删Token问题
    this.checkAndCleanInvalidToken();
    // 2. 注册当前标签（刷新/新标签都执行）
    this.registerTab();
    // 3. 绑定事件
    this.bindEvents();
    this.listenRouter(router);
    this.startIdleCheck();
    this.listenStorageChange();
    // 4. 挂载
    Vue.prototype.$tokenManager = this;
    window.$vueRouter = router;
  }

  // ========== 核心：标签ID管理 ==========
  getTabId() {
    let tabId = sessionStorage.getItem(this.config.tabIdKey);
    if (!tabId) {
      tabId = `tab_${Date.now()}_${Math.random().toString(36).slice(-8)}`;
      sessionStorage.setItem(this.config.tabIdKey, tabId);
    }
    return tabId;
  }

  getAliveTabs() {
    try {
      return JSON.parse(localStorage.getItem(this.config.aliveTabsKey) || "[]");
    } catch (e) {
      return [];
    }
  }

  registerTab() {
    const aliveTabs = this.getAliveTabs();
    if (!aliveTabs.includes(this.tabId)) {
      aliveTabs.push(this.tabId);
      localStorage.setItem(this.config.aliveTabsKey, JSON.stringify(aliveTabs));
    }
    // 清除可能的延时清理定时器（刷新/新标签时）
    this.clearCleanupTimer();
  }

  // ========== 核心：关闭/刷新处理 ==========
  bindEvents() {
    // 1. 监听用户操作
    ["mousemove", "keydown", "click", "scroll"].forEach((event) => {
      window.addEventListener(event, this.updateLastOperate);
    });

    // 2. 页面卸载：仅标记移除，延时清理（给刷新留时间）
    window.addEventListener("beforeunload", () => {
      this.markTabForRemoval();
    });

    // 3. 页面加载：刷新场景立即恢复标签（核心！）
    window.addEventListener("load", () => {
      this.registerTab(); // 刷新后重新注册，确保列表不为空
      this.clearCleanupTimer();
    });
  }

  // 标记标签待移除，延时2秒清理（刷新时会被load事件取消）
  markTabForRemoval() {
    this.clearCleanupTimer();
    console.log("触发markTabForRemoval，准备延时清理", this.tabId); // 新增日志
    this.cleanupTimer = setTimeout(() => {
      const aliveTabs = this.getAliveTabs().filter((id) => id !== this.tabId);
      console.log("延时清理执行，剩余存活标签：", aliveTabs); // 新增日志
      localStorage.setItem(this.config.aliveTabsKey, JSON.stringify(aliveTabs));
      if (aliveTabs.length === 0) {
        this.clearToken();
        console.log("无存活标签，清理Token");
      }
    }, this.config.cleanupDelay);
  }

  clearCleanupTimer() {
    if (this.cleanupTimer) {
      clearTimeout(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }

  // ========== 核心：启动校验（解决浏览器关闭不删Token） ==========
  checkAndCleanInvalidToken() {
    const token = localStorage.getItem(this.config.tokenKey);
    const aliveTabs = this.getAliveTabs();

    // 场景1：有Token但无存活标签 → 浏览器重启，清理Token
    if (token && aliveTabs.length === 0) {
      this.clearToken();
      this.logout();
      console.log("浏览器重启，清理无效Token");
      return;
    }

    // 场景2：有Token但当前标签不在存活列表 → 刷新/新标签，重新注册
    if (token && !aliveTabs.includes(this.tabId)) {
      this.registerTab();
    }
  }

  // ========== 原有逻辑（无修改） ==========
  listenRouter(router) {
    router.afterEach(() => {
      localStorage.setItem(this.config.lastOperateKey, Date.now());
    });
  }

  listenStorageChange() {
    window.addEventListener("storage", (e) => {
      if (e.key === this.config.tokenKey && !e.newValue && !this.isLoggedOut) {
        this.logout();
      }
    });
  }

  startIdleCheck() {
    this.stopIdleCheck();
    this.idleTimer = setInterval(() => this.checkIdleTimeout(), 1000);
  }

  stopIdleCheck() {
    if (this.idleTimer) clearInterval(this.idleTimer);
  }

  checkIdleTimeout() {
    if (this.isLoggedOut) return;
    const token = this.getToken();
    if (!token) return this.logout();

    const lastOperate = localStorage.getItem(this.config.lastOperateKey);
    const idleTime = lastOperate
      ? Date.now() - Number(lastOperate)
      : this.config.timeout;
    if (idleTime >= this.config.timeout) {
      this.logout();
    }
  }

  setToken(token) {
    this.isLoggedOut = false;
    localStorage.setItem(this.config.tokenKey, token);
    localStorage.setItem(this.config.lastOperateKey, Date.now());
    this.registerTab();
    this.startIdleCheck();
  }

  getToken() {
    return localStorage.getItem(this.config.tokenKey);
  }

  clearToken() {
    localStorage.removeItem(this.config.tokenKey);
    localStorage.removeItem(this.config.lastOperateKey);
    localStorage.removeItem(this.config.aliveTabsKey);
  }

  logout() {
    console.log('111')
    console.log(this.isLoggedOut)
    if (this.isLoggedOut) return;
    this.isLoggedOut = true;
    this.clearToken();
    this.clearCleanupTimer();
    this.stopIdleCheck();
    if (window.$vueRouter) {
      window.$vueRouter.push({ path: "/login" })
    } else {
      window.location.href = "/login";
    }
  }

  manualLogout() {
    this.logout();
  }

  debounce(fn, delay) {
    let timer = null;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }
}

const tokenManager = new TokenManager();
export default tokenManager;
